/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import "./Chat.scss";
import { PaperClipOutlined } from "@ant-design/icons";
import ChatGPTSpeechBubbleAvatar from "./ChatGPTSpeechBubbleAvatar";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { VisionContentPart, INTERNAL_ATTACHMENT_PREFIX } from "./attachmentUtils";

interface DisplayMeta {
    fileName?: string;
    attachmentPreview?: string;
    isInformalUpload?: boolean;
}

interface ISpeechBubble {
    content: string | VisionContentPart[];
    isLocal: boolean;
    displayMeta?: DisplayMeta;
    onAutoResponse: (text: string) => void;
}

const getDisplayText = (
    content: string | VisionContentPart[],
    displayMeta?: DisplayMeta
): string => {
    if (typeof content === "string") {
        return content;
    }

    const textPart = content.find((part) => part.type === "text");
    if (textPart && textPart.type === "text") {
        return textPart.text;
    }

    if (displayMeta?.fileName) {
        return `Attached: ${displayMeta.fileName}`;
    }

    return "Attached quotation";
};

const getPreviewUrl = (
    content: string | VisionContentPart[],
    displayMeta?: DisplayMeta
): string | undefined => {
    if (displayMeta?.attachmentPreview) {
        return displayMeta.attachmentPreview;
    }

    if (!Array.isArray(content)) {
        return undefined;
    }

    const imagePart = content.find((part) => part.type === "image_url");
    if (imagePart && imagePart.type === "image_url") {
        const url = imagePart.image_url.url;
        if (url.startsWith("data:")) {
            return url;
        }
        if (url.startsWith(INTERNAL_ATTACHMENT_PREFIX)) {
            return undefined;
        }
    }

    return undefined;
};

const ChatGPTSpeechBubble: React.FC<ISpeechBubble> = ({
    isLocal,
    content,
    displayMeta,
    onAutoResponse,
}) => {
    const [fromMe, setFormMe] = useState<boolean>(true);

    useEffect(() => {
        setFormMe(isLocal);
        return () => {
            setFormMe(false);
        };
    }, [isLocal]);

    const generateArticleClassName = useMemo((): string => {
        return `from-${fromMe ? "me" : "them"}`;
    }, [fromMe]);

    const displayText = getDisplayText(content, displayMeta);
    const previewUrl = getPreviewUrl(content, displayMeta);
    const fileName = displayMeta?.fileName;

    const MarkdownRenderer = ({ markdown }: { markdown: string }) => {
        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ({ node, ...props }) => (
                        <div className="table-container">
                            <table className="grid-table" {...props} />
                        </div>
                    ),
                    th: ({ node, ...props }) => (
                        <th className="border border-gray-300 bg-gray-100 p-2" {...props} />
                    ),
                    td: ({ node, ...props }) => (
                        <td className="border border-gray-300 p-2" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                        <h3 className="text-xl font-bold mt-4 mb-2" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                        <ul className="list-disc pl-6 mb-4" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                        <li className="mb-1" {...props} />
                    ),
                }}
            >
                {markdown}
            </ReactMarkdown>
        );
    };

    return (
        <div className={` sai-bubble-wrapper ${isLocal ? "out" : "in"}`}>
            {!fromMe && <ChatGPTSpeechBubbleAvatar />}
            <article className={generateArticleClassName}>
                {isLocal && (previewUrl || fileName) && (
                    <div
                        style={{
                            marginBottom: 12,
                            padding: 8,
                            borderRadius: 8,
                            background: "#f5f5f5",
                        }}
                    >
                        {previewUrl && (
                            <img
                                src={previewUrl}
                                alt={fileName || "Attachment preview"}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: 160,
                                    borderRadius: 4,
                                    marginBottom: fileName ? 8 : 0,
                                }}
                            />
                        )}
                        {fileName && (
                            <div style={{ fontSize: 13, color: "#555" }}>
                                <PaperClipOutlined /> {fileName}
                            </div>
                        )}
                    </div>
                )}

                {!isLocal && <MarkdownRenderer markdown={displayText} />}

                {isLocal && displayText && (
                    <div style={{ whiteSpace: "pre-wrap" }}>{displayText}</div>
                )}

                {!fromMe && (
                    <div style={{ marginTop: "20px" }}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ cursor: "pointer", opacity: "0.7", marginRight: "16px" }}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M7 5C7 3.34315 8.34315 2 10 2H19C20.6569 2 22 3.34315 22 5V14C22 15.6569 20.6569 17 19 17H17V19C17 20.6569 15.6569 22 14 22H5C3.34315 22 2 20.6569 2 19V10C2 8.34315 3.34315 7 5 7H7V5ZM9 7H14C15.6569 7 17 8.34315 17 10V15H19C19.5523 15 20 14.5523 20 14V5C20 4.44772 19.5523 4 19 4H10C9.44772 4 9 4.44772 9 5V7ZM5 9C4.44772 9 4 9.44772 4 10V19C4 19.5523 4.44772 20 5 20H14C14.5523 20 15 19.5523 15 19V10C15 9.44772 14.5523 9 14 9H5Z"
                                fill="currentColor"
                            />
                        </svg>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ cursor: "pointer", opacity: "0.7", marginRight: "16px" }}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M12.1318 2.50389C12.3321 2.15338 12.7235 1.95768 13.124 2.00775L13.5778 2.06447C16.0449 2.37286 17.636 4.83353 16.9048 7.20993L16.354 8.99999H17.0722C19.7097 8.99999 21.6253 11.5079 20.9313 14.0525L19.5677 19.0525C19.0931 20.7927 17.5124 22 15.7086 22H6C4.34315 22 3 20.6568 3 19V12C3 10.3431 4.34315 8.99999 6 8.99999H8C8.25952 8.99999 8.49914 8.86094 8.6279 8.63561L12.1318 2.50389ZM10 20H15.7086C16.6105 20 17.4008 19.3964 17.6381 18.5262L19.0018 13.5262C19.3488 12.2539 18.391 11 17.0722 11H15C14.6827 11 14.3841 10.8494 14.1956 10.5941C14.0071 10.3388 13.9509 10.0092 14.0442 9.70591L14.9932 6.62175C15.3384 5.49984 14.6484 4.34036 13.5319 4.08468L10.3644 9.62789C10.0522 10.1742 9.56691 10.5859 9 10.8098V19C9 19.5523 9.44772 20 10 20ZM7 11V19C7 19.3506 7.06015 19.6872 7.17071 20H6C5.44772 20 5 19.5523 5 19V12C5 11.4477 5.44772 11 6 11H7Z"
                                fill="currentColor"
                            />
                        </svg>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ cursor: "pointer", opacity: "0.7" }}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M11.8727 21.4961C11.6725 21.8466 11.2811 22.0423 10.8805 21.9922L10.4267 21.9355C7.95958 21.6271 6.36855 19.1665 7.09975 16.7901L7.65054 15H6.93226C4.29476 15 2.37923 12.4921 3.0732 9.94753L4.43684 4.94753C4.91145 3.20728 6.49209 2 8.29589 2H18.0045C19.6614 2 21.0045 3.34315 21.0045 5V12C21.0045 13.6569 19.6614 15 18.0045 15H16.0045C15.745 15 15.5054 15.1391 15.3766 15.3644L11.8727 21.4961ZM14.0045 4H8.29589C7.39399 4 6.60367 4.60364 6.36637 5.47376L5.00273 10.4738C4.65574 11.746 5.61351 13 6.93226 13H9.00451C9.32185 13 9.62036 13.1506 9.8089 13.4059C9.99743 13.6612 10.0536 13.9908 9.96028 14.2941L9.01131 17.3782C8.6661 18.5002 9.35608 19.6596 10.4726 19.9153L13.6401 14.3721C13.9523 13.8258 14.4376 13.4141 15.0045 13.1902V5C15.0045 4.44772 14.5568 4 14.0045 4ZM17.0045 13V5C17.0045 4.64937 16.9444 4.31278 16.8338 4H18.0045C18.5568 4 19.0045 4.44772 19.0045 5V12C19.0045 12.5523 18.5568 13 18.0045 13H17.0045Z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                )}
            </article>
        </div>
    );
};

export default ChatGPTSpeechBubble;
